/**
 * NGX Stock Ratings Database
 *
 * Comprehensive list of Nigerian Stock Exchange (NGX) listed companies
 * with mock credit/investment ratings from Agusto & Co., GCR Ratings,
 * DataPro Limited, and Fitch Ratings.
 *
 * Covers all major sectors: Banking, Telecoms, Oil & Gas, Manufacturing,
 * Consumer Goods, Real Estate, Healthcare, Insurance, Agriculture, and more.
 */

export interface CompanyRating {
  symbol: string;
  company: string;
  sector: string;
  agusto?: string;
  gcr?: string;
  datapro?: string;
  fitch?: string;
  outlook?: 'Stable' | 'Positive' | 'Negative' | 'Watch';
  ratingDate?: string;
}

const NGX_COMPANIES: CompanyRating[] = [
  // BANKING & FINANCIAL SERVICES
  { symbol: 'ZENITHBANK', company: 'Zenith Bank Plc', sector: 'Banking', agusto: 'A', gcr: 'A-', datapro: 'A', outlook: 'Stable', ratingDate: '2024-08-15' },
  { symbol: 'GUARANTY', company: 'Guaranty Trust Holding Company', sector: 'Banking', agusto: 'A+', gcr: 'A', datapro: 'A+', fitch: 'B+', outlook: 'Stable', ratingDate: '2024-08-20' },
  { symbol: 'FIRSTBANK', company: 'First Bank of Nigeria Holdings', sector: 'Banking', agusto: 'A', gcr: 'A-', datapro: 'A', outlook: 'Stable', ratingDate: '2024-08-10' },
  { symbol: 'STERLING', company: 'Sterling Bank Plc', sector: 'Banking', agusto: 'BBB+', gcr: 'BBB', datapro: 'BBB+', outlook: 'Stable', ratingDate: '2024-08-18' },
  { symbol: 'STANBIC', company: 'Stanbic IBTC Holdings Plc', sector: 'Banking', agusto: 'AA-', gcr: 'AA', datapro: 'AA-', outlook: 'Stable', ratingDate: '2024-08-22' },
  { symbol: 'UNITYBK', company: 'Unity Bank Plc', sector: 'Banking', agusto: 'BB+', gcr: 'BB', datapro: 'BB+', outlook: 'Positive', ratingDate: '2024-09-01' },
  { symbol: 'WAPCO', company: 'Wapic Insurance Plc', sector: 'Insurance', agusto: 'BBB', gcr: 'BB+', datapro: 'BBB', outlook: 'Stable', ratingDate: '2024-08-25' },
  { symbol: 'SEPLAT', company: 'Seplat Petroleum Development Company', sector: 'Oil & Gas', agusto: 'BBB-', gcr: 'BB+', datapro: 'BBB-', outlook: 'Negative', ratingDate: '2024-08-20' },

  // TELECOMS
  { symbol: 'MTNN', company: 'Airtel Africa (formerly Airtel Networks Nigeria)', sector: 'Telecoms', agusto: 'A+', gcr: 'A', datapro: 'A', fitch: 'A-', outlook: 'Stable', ratingDate: '2024-08-10' },
  { symbol: 'AIRTELAFRI', company: 'Airtel Africa Limited', sector: 'Telecoms', agusto: 'A', gcr: 'A-', datapro: 'A-', fitch: 'B+', outlook: 'Stable', ratingDate: '2024-08-15' },

  // MANUFACTURING & CONGLOMERATES
  { symbol: 'DANGCEM', company: 'Dangote Cement Plc', sector: 'Manufacturing', agusto: 'BBB+', gcr: 'BBB', datapro: 'BBB+', fitch: 'B+', outlook: 'Positive', ratingDate: '2024-08-20' },
  { symbol: 'BUACEMENT', company: 'BUA Cement Plc', sector: 'Manufacturing', agusto: 'BBB', gcr: 'BBB-', datapro: 'BBB+', outlook: 'Stable', ratingDate: '2024-08-18' },
  { symbol: 'MTNN', company: 'Multiverse Technology and Networks Nigeria', sector: 'Technology', agusto: 'BBB-', gcr: 'BB+', datapro: 'BBB-', outlook: 'Stable', ratingDate: '2024-08-12' },
  { symbol: 'NASCON', company: 'National Salt Company of Nigeria Plc', sector: 'Manufacturing', agusto: 'BBB', gcr: 'BB+', datapro: 'BBB', outlook: 'Stable', ratingDate: '2024-08-22' },

  // CONSUMER GOODS
  { symbol: 'NESTLE', company: 'Nestle Nigeria Plc', sector: 'Consumer Goods', agusto: 'A-', gcr: 'A', datapro: 'A-', outlook: 'Stable', ratingDate: '2024-08-15' },
  { symbol: 'UNILEVER', company: 'Unilever Nigeria Plc', sector: 'Consumer Goods', agusto: 'A', gcr: 'A-', datapro: 'A', outlook: 'Stable', ratingDate: '2024-08-20' },
  { symbol: 'FLOURMILL', company: 'Flour Mills of Nigeria Plc', sector: 'Consumer Goods', agusto: 'BBB+', gcr: 'BBB', datapro: 'BBB+', outlook: 'Stable', ratingDate: '2024-08-18' },
  { symbol: 'PRESCO', company: 'Presco Plc', sector: 'Agriculture', agusto: 'BB+', gcr: 'BB', datapro: 'BB+', outlook: 'Stable', ratingDate: '2024-08-25' },
  { symbol: 'IKUNLEKO', company: 'IKEJA Hotel Plc', sector: 'Hospitality', agusto: 'BB', gcr: 'BB-', datapro: 'BB', outlook: 'Positive', ratingDate: '2024-09-01' },

  // OIL & GAS
  { symbol: 'ARADEL', company: 'Aradel Holdings Plc', sector: 'Oil & Gas', agusto: 'BBB-', gcr: 'BB', datapro: 'BBB-', outlook: 'Stable', ratingDate: '2024-08-20' },
  { symbol: 'GEREGU', company: 'Geregu Power Plc', sector: 'Energy', agusto: 'BB+', gcr: 'BB', datapro: 'BB+', outlook: 'Positive', ratingDate: '2024-08-22' },

  // REAL ESTATE & PROPERTY
  { symbol: 'SFSREIT', company: 'Sigma Real Estate Investment Trust', sector: 'Real Estate', agusto: 'BBB', gcr: 'BB+', datapro: 'BBB', outlook: 'Stable', ratingDate: '2024-08-20' },
  { symbol: 'UNIONDMR', company: 'Union Homes Realtor Plc', sector: 'Real Estate', agusto: 'BB', gcr: 'BB-', datapro: 'BB', outlook: 'Stable', ratingDate: '2024-08-18' },

  // HEALTHCARE
  { symbol: 'PHARMAAF', company: 'Pharmaceutical Manufacturers Association', sector: 'Healthcare', agusto: 'BBB+', gcr: 'BBB', datapro: 'BBB+', outlook: 'Stable', ratingDate: '2024-08-25' },

  // FINANCIAL SERVICES (NON-BANK)
  { symbol: 'FBNH', company: 'First Bank Holdings', sector: 'Banking', agusto: 'A-', gcr: 'A-', datapro: 'A-', outlook: 'Stable', ratingDate: '2024-08-20' },
  { symbol: 'GOLDINSURE', company: 'Gold Insurance Plc', sector: 'Insurance', agusto: 'BB', gcr: 'BB-', datapro: 'BB', outlook: 'Stable', ratingDate: '2024-08-22' },

  // INDUSTRIALS & DIVERSIFIED
  { symbol: 'BETAGLAS', company: 'Beta Glass Company Plc', sector: 'Manufacturing', agusto: 'BBB-', gcr: 'BB+', datapro: 'BBB-', outlook: 'Stable', ratingDate: '2024-08-18' },
  { symbol: 'ELEMES', company: 'Electron Industries Plc', sector: 'Manufacturing', agusto: 'BB', gcr: 'BB-', datapro: 'BB', outlook: 'Watch', ratingDate: '2024-08-20' },
  { symbol: 'EUNISELL', company: 'Eunisell Limited', sector: 'Distribution', agusto: 'BB+', gcr: 'BB', datapro: 'BB+', outlook: 'Stable', ratingDate: '2024-08-15' },

  // TRANSPORTATION & LOGISTICS
  { symbol: 'TRANSPOWER', company: 'Transnational Corporation of Nigeria Plc', sector: 'Energy', agusto: 'BB+', gcr: 'BB', datapro: 'BB+', outlook: 'Stable', ratingDate: '2024-08-22' },

  // AGRICULTURE
  { symbol: 'BUAFOODS', company: 'BUA Foods Plc', sector: 'Agriculture', agusto: 'A-', gcr: 'A', datapro: 'A-', fitch: 'B+', outlook: 'Positive', ratingDate: '2024-08-18' },
  { symbol: 'OANDO', company: 'Oando Plc', sector: 'Oil & Gas', agusto: 'BBB-', gcr: 'BB', datapro: 'BBB-', outlook: 'Negative', ratingDate: '2024-08-20' },

  // UTILITIES
  { symbol: 'APLC', company: 'Asset Property Leasing Company', sector: 'Utilities', agusto: 'BBB', gcr: 'BB+', datapro: 'BBB', outlook: 'Stable', ratingDate: '2024-08-25' },

  // TECHNOLOGY
  { symbol: 'CAVERTON', company: 'Caverton Offshore Support Group Plc', sector: 'Technology', agusto: 'BB', gcr: 'BB-', datapro: 'BB', outlook: 'Stable', ratingDate: '2024-08-20' },

  // GOVERNMENT BONDS & FIXED INCOME
  { symbol: 'FGN29-13.53', company: 'Federal Government of Nigeria Bond 2029', sector: 'Sovereign Debt', agusto: 'AA-', gcr: 'AA', datapro: 'AA-', fitch: 'B+', outlook: 'Stable', ratingDate: '2024-09-01' },
  { symbol: 'FGN28-12.75', company: 'Federal Government of Nigeria Bond 2028', sector: 'Sovereign Debt', agusto: 'AA-', gcr: 'AA', datapro: 'AA-', fitch: 'B+', outlook: 'Stable', ratingDate: '2024-09-01' },

  // ETFs
  { symbol: 'NGXASI', company: 'NGX All-Share Index', sector: 'ETF', agusto: 'BBB+', gcr: 'BBB', datapro: 'BBB+', outlook: 'Stable', ratingDate: '2024-08-30' },
  { symbol: 'NGXSECURE', company: 'NGX Secured Bond Fund', sector: 'ETF', agusto: 'AA-', gcr: 'AA', datapro: 'AA-', outlook: 'Stable', ratingDate: '2024-08-20' },
  { symbol: 'NGXESMALL', company: 'NGX Emerging Markets ETF', sector: 'ETF', agusto: 'BBB', gcr: 'BB+', datapro: 'BBB', outlook: 'Positive', ratingDate: '2024-08-25' },

  // ADDITIONAL MAJOR STOCKS
  { symbol: 'GTCO', company: 'Guaranty Trust Company', sector: 'Banking', agusto: 'A+', gcr: 'A', datapro: 'A+', fitch: 'B+', outlook: 'Stable', ratingDate: '2024-08-20' },
  { symbol: 'ACCESSCORP', company: 'Access Holdings Plc', sector: 'Banking', agusto: 'A', gcr: 'A-', datapro: 'A', outlook: 'Stable', ratingDate: '2024-08-18' },
  { symbol: 'FCMB', company: 'FCMB Group Plc', sector: 'Banking', agusto: 'BBB+', gcr: 'BBB', datapro: 'BBB+', outlook: 'Stable', ratingDate: '2024-08-22' },
  { symbol: 'UACN', company: 'United Africa Company', sector: 'Conglomerate', agusto: 'BBB', gcr: 'BB+', datapro: 'BBB', outlook: 'Stable', ratingDate: '2024-08-20' },
  { symbol: 'WAPCO', company: 'Wapic Insurance Plc', sector: 'Insurance', agusto: 'BB+', gcr: 'BB', datapro: 'BB+', outlook: 'Stable', ratingDate: '2024-08-25' },
  { symbol: 'SOVEREIGN', company: 'Sovereign Trust Insurance', sector: 'Insurance', agusto: 'BB', gcr: 'BB-', datapro: 'BB', outlook: 'Stable', ratingDate: '2024-08-22' },
  { symbol: 'CORNERSTONE', company: 'Cornerstone Insurance Plc', sector: 'Insurance', agusto: 'BB-', gcr: 'BB-', datapro: 'BB-', outlook: 'Negative', ratingDate: '2024-08-20' },
  { symbol: 'RTBRINS', company: 'Regency Assured Insurance', sector: 'Insurance', agusto: 'BB-', gcr: 'BB-', datapro: 'BB-', outlook: 'Watch', ratingDate: '2024-08-18' },
  { symbol: 'TOTAL', company: 'Total Nigeria Plc', sector: 'Oil & Gas', agusto: 'BBB', gcr: 'BBB-', datapro: 'BBB', fitch: 'B', outlook: 'Stable', ratingDate: '2024-08-20' },
  { symbol: 'AIRTELAFRIC', company: 'Airtel Africa Limited', sector: 'Telecoms', agusto: 'A-', gcr: 'A-', datapro: 'A-', fitch: 'B+', outlook: 'Stable', ratingDate: '2024-08-15' },
];

export function getAllNGXRatings() {
  return NGX_COMPANIES.map((company) => {
    const ratings = [];
    if (company.agusto) {
      ratings.push({
        agency: 'Agusto & Co.',
        symbol: company.symbol,
        company: company.company,
        assetType: company.sector === 'ETF' ? 'ETF' : company.sector === 'Sovereign Debt' ? 'Bond' : 'Stock' as const,
        rating: company.agusto,
        outlook: company.outlook || 'Stable' as const,
        ratingDate: company.ratingDate || '2024-08-15',
        nextReview: new Date(new Date(company.ratingDate || '2024-08-15').getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        rationale: generateRationale(company.symbol, company.agusto, company.sector),
      });
    }
    if (company.gcr) {
      ratings.push({
        agency: 'GCR Ratings',
        symbol: company.symbol,
        company: company.company,
        assetType: company.sector === 'ETF' ? 'ETF' : company.sector === 'Sovereign Debt' ? 'Bond' : 'Stock' as const,
        rating: company.gcr,
        outlook: company.outlook || 'Stable' as const,
        ratingDate: company.ratingDate || '2024-08-20',
        nextReview: new Date(new Date(company.ratingDate || '2024-08-20').getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        rationale: generateRationale(company.symbol, company.gcr, company.sector),
      });
    }
    if (company.datapro) {
      ratings.push({
        agency: 'DataPro Limited',
        symbol: company.symbol,
        company: company.company,
        assetType: company.sector === 'ETF' ? 'ETF' : company.sector === 'Sovereign Debt' ? 'Bond' : 'Stock' as const,
        rating: company.datapro,
        outlook: company.outlook || 'Stable' as const,
        ratingDate: company.ratingDate || '2024-07-15',
        nextReview: new Date(new Date(company.ratingDate || '2024-07-15').getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        rationale: generateRationale(company.symbol, company.datapro, company.sector),
      });
    }
    if (company.fitch) {
      ratings.push({
        agency: 'Fitch Ratings',
        symbol: company.symbol,
        company: company.company,
        assetType: company.sector === 'ETF' ? 'ETF' : company.sector === 'Sovereign Debt' ? 'Bond' : 'Stock' as const,
        rating: company.fitch,
        outlook: company.outlook || 'Stable' as const,
        ratingDate: company.ratingDate || '2024-06-15',
        nextReview: new Date(new Date(company.ratingDate || '2024-06-15').getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        rationale: generateRationale(company.symbol, company.fitch, company.sector),
      });
    }
    return ratings;
  }).flat();
}

function generateRationale(symbol: string, rating: string, sector: string): string {
  const ratingQuality = rating.includes('A') ? 'strong' : rating.includes('B') ? 'moderate' : 'developing';
  const sectorContexts: Record<string, string> = {
    'Banking': 'solid capital adequacy and asset quality',
    'Telecoms': 'strong market position and cash generation',
    'Oil & Gas': 'commodity exposure with volatility management',
    'Manufacturing': 'competitive positioning and margin control',
    'Consumer Goods': 'brand strength and market demand',
    'Real Estate': 'property valuation and rental income stability',
    'Healthcare': 'regulatory compliance and demand resilience',
    'Insurance': 'claims reserves adequacy and capital strength',
    'Agriculture': 'commodity price exposure and supply chain',
    'Energy': 'power generation reliability and payment security',
    'Technology': 'operational efficiency and innovation pipeline',
    'ETF': 'underlying asset quality and diversification',
    'Sovereign Debt': 'fiscal management and external liquidity',
  };
  const context = sectorContexts[sector] || 'market performance and business fundamentals';
  return `${ratingQuality.charAt(0).toUpperCase() + ratingQuality.slice(1)} ${sector.toLowerCase()} player with ${context}`;
}
